import { useRef, useEffect, useState, useMemo } from 'react';
import {isEqual} from 'lodash';
import { message } from 'antd';
import {getLang} from '@/utils/tools'

// 1. 创建全局翻译缓存（避免重复请求相同内容）
const translationCache = new Map();

// 2. 创建全局请求队列（避免并发请求相同内容）
const pendingRequests = new Map();

const UseTranslateGoogleHook = ({ keys, debounceTime = 300 }) => {
    // 使用 useRef 存储稳定的 keys 对象引用
    const keysRef = useRef(keys);

    // 存储翻译结果的状态
    const [langs, setLangs] = useState({});

    // 存储请求错误的状态
    const [error, setError] = useState(null);

    // 存储请求加载中的状态
    const [loading, setLoading] = useState(false);

    // 使用 useMemo 生成稳定的 keys 对象（深度比较）
    const stableKeys = useMemo(() => {
        // 如果新 keys 与旧 keys 内容相同，则返回旧引用
        if (isEqual(keys, keysRef.current)) {
            return keysRef.current;
        }
        // 否则更新引用并返回新 keys
        keysRef.current = keys;
        return keys;
    }, [keys]);

    // 防抖处理请求（避免短时间内重复请求）
    const debounceRequest = useRef(null);

    // 处理翻译请求的函数
    const fetchTranslation = async () => {
        // 如果 keys 为空则直接返回
        if (!stableKeys || stableKeys.length === 0) {
            return;
        }

        // 将 keys 数组转换为字符串作为缓存键
        const cacheKey = stableKeys?.join?.('|');
        // const cacheKey = Array.isArray(stableKeys) ? stableKeys?.join('|'):'';

        // 检查缓存中是否已有结果
        if (translationCache.has(cacheKey)) {
            setLangs(translationCache.get(cacheKey));
            return;
        }

        // 检查是否已有正在进行的相同请求
        if (pendingRequests.has(cacheKey)) {
            try {
                // 等待已有请求完成
                const result = await pendingRequests.get(cacheKey);
                setLangs(result);
            } catch (err) {
                setError(err);
            }
            return;
        }

        // 开始请求，设置加载状态
        setLoading(true);

        // 创建一个 Promise 表示正在进行的请求
        const requestPromise = getLang({ keys: stableKeys })
            .then(data => {
                // 存储到缓存
                translationCache.set(cacheKey, data);
                // 更新状态
                setLangs(data);
                return data;
            })
            .catch(err => {
                console.error('翻译请求失败:', err);
                setError(err);
                message.error('翻译服务暂时不可用');
                throw err;
            })
            .finally(() => {
                // 请求完成后从队列中移除
                pendingRequests.delete(cacheKey);
                setLoading(false);
            });

        // 将请求添加到队列
        pendingRequests.set(cacheKey, requestPromise);
    };

    // 使用 useEffect 监听 stableKeys 变化
    useEffect(() => {
        // 清除之前的防抖计时器
        if (debounceRequest.current) {
            clearTimeout(debounceRequest.current);
        }

        // 设置新的防抖计时器
        debounceRequest.current = setTimeout(() => {
            fetchTranslation();
        }, debounceTime);

        // 组件卸载时清除计时器
        return () => {
            if (debounceRequest.current) {
                clearTimeout(debounceRequest.current);
            }
        };
    }, [stableKeys, debounceTime]);

    return { langs, loading, error };
};

export default UseTranslateGoogleHook;